import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";

interface SetFallbackOwnerBody {
  fallback_owner_user_id: number | null;
}

interface SetFallbackOwnerResponse {
  workspace_id: number;
  fallback_owner_user_id: number | null;
}

/**
 * Set the workspace fallback owner used to inherit workspace-owned
 * entities (prompts, datasets, ...) when a user is deprovisioned.
 * Requires workspace Admin permission.
 */
export const useSetWorkspaceFallbackOwner = (workspaceId: number) => {
  const userToken = useAuth()?.userToken;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetFallbackOwnerBody): Promise<SetFallbackOwnerResponse> =>
      fetch(ENDPOINTS.identity.workspace_fallback_owner(workspaceId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken ?? ""}`,
        },
        body: JSON.stringify(body),
      }).then(handleResponse),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "members"] });
    },
  });
};
